import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Plus, UserCog } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { getApiErrorMessage } from '../../lib/apiError';

const statusStyles = {
  active: 'bg-green-100 text-green-700 border-green-200',
  suspended: 'bg-red-100 text-red-700 border-red-200',
  terminated: 'bg-gray-100 text-gray-600 border-gray-200'
};

const CareOfficersListPage = () => {
  const { authApi } = useAuth();
  const [careOfficers, setCareOfficers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const response = await authApi.get('/care-officers/managed');
        if (mounted) setCareOfficers(response.data || []);
      } catch (err) {
        if (mounted) setError(getApiErrorMessage(err, 'Unable to load care officers.'));
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [authApi]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-serif text-3xl font-medium text-[#1A1A1A]">Care Officers</h1>
          <p className="mt-1 text-sm text-[#5A5A5A]">Create and manage field officers you onboarded.</p>
        </div>
        <Button asChild>
          <Link to="/care-officers/create">
            <Plus className="h-4 w-4" />
            Create Care Officer
          </Link>
        </Button>
      </div>

      {error && (
        <div className="mb-5 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="rounded-lg border border-[#E8E6E3] bg-white">
        {loading ? (
          <div className="flex items-center justify-center p-12 text-[#5A5A5A]">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Loading care officers...
          </div>
        ) : careOfficers.length === 0 ? (
          <div className="p-12 text-center">
            <UserCog className="mx-auto mb-4 h-12 w-12 text-gray-300" />
            <p className="text-sm text-[#5A5A5A]">No care officers created yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-[#E8E6E3] bg-[#FAFAF7] text-left text-xs uppercase tracking-wide text-[#5A5A5A]">
                <tr>
                  <th className="px-5 py-4">Name</th>
                  <th className="px-5 py-4">Email</th>
                  <th className="px-5 py-4">Territory</th>
                  <th className="px-5 py-4">Rating</th>
                  <th className="px-5 py-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8E6E3]">
                {careOfficers.map(careOfficer => (
                  <tr key={careOfficer.id}>
                    <td className="px-5 py-4 font-medium text-[#1A1A1A]">
                      {careOfficer.firstName || careOfficer.first_name} {careOfficer.lastName || careOfficer.last_name}
                    </td>
                    <td className="px-5 py-4 text-sm text-[#5A5A5A]">{careOfficer.email}</td>
                    <td className="px-5 py-4 text-sm text-[#5A5A5A]">{careOfficer.preferredTerritory || careOfficer.assignedRegion || '-'}</td>
                    <td className="px-5 py-4 text-sm text-[#5A5A5A]">
                      {(careOfficer.averageRating || careOfficer.rating || 0).toFixed ? (careOfficer.averageRating || careOfficer.rating || 0).toFixed(1) : careOfficer.averageRating || careOfficer.rating || 0}
                      <span className="ml-1 text-xs">({careOfficer.ratingCount || 0})</span>
                    </td>
                    <td className="px-5 py-4">
                      <Badge className={`${statusStyles[careOfficer.status] || 'bg-gray-100 text-gray-700 border-gray-200'} border`}>
                        {careOfficer.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CareOfficersListPage;
